import { useMutation, useQueryClient } from "@tanstack/react-query";
import { ChevronRight, CircleCheck, CircleX, Diamond, Inbox, Play } from "lucide-react";
import { useState } from "react";
import { Link, useNavigate } from "react-router";
import { toast } from "sonner";
import { api } from "../../api";
import { describeNext, describePipeline, issueKey, triggerText } from "../../lib/tasks";
import { useFactory } from "../../store";
import type { AgentSummary } from "../../types";
import { DEFAULT_REPO, RepoSelect, useRepos } from "../issues/repo-select";
import { MergeTicket } from "../merge-ticket";
import { ColorDot, StatusBadge, ToneBadge } from "../status-badge";
import { TimeAgo } from "../time-ago";
import { Badge } from "../ui/badge";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { ScrollArea } from "../ui/scroll-area";
import { Separator } from "../ui/separator";
import { SheetDescription, SheetHeader, SheetTitle } from "../ui/sheet";
import { Spinner } from "../ui/spinner";

export function AgentDetail({ agent }: { agent: AgentSummary }) {
  const { state, layout, agentById } = useFactory();
  const stats = state?.stations[agent.id];
  const section = layout?.sections.find((s) => s.id === agent.section);
  const runs = (state?.runs ?? [])
    .filter((r) => r.agentId === agent.id)
    .sort((a, b) => b.createdAt.localeCompare(a.createdAt))
    .slice(0, 12);
  const tasks = new Map((state?.tasks ?? []).map((t) => [t.id, t]));
  const mergeItems =
    agent.policy === "merge-queue"
      ? (state?.mergeQueue ?? []).filter((m) => !["merged", "cancelled", "failed"].includes(m.status))
      : [];
  const pipeline = layout ? describePipeline(agent.id, layout, agentById) : undefined;

  return (
    <>
      <SheetHeader className="border-b p-6">
        <p className="flex items-center gap-2 text-xs font-medium text-muted-foreground">
          {section && <ColorDot color={section.color} className="size-2 ring-0" />}
          {section?.name} section
        </p>
        <SheetTitle className="text-xl">{agent.name}</SheetTitle>
        <SheetDescription>{agent.description}</SheetDescription>
        <div className="flex flex-wrap gap-1.5 pt-1">
          <Badge variant="secondary">
            {agent.kind === "policy" ? `Policy · ${agent.policy}` : agent.provider}
          </Badge>
          {agent.model && <Badge variant="outline">{agent.model}</Badge>}
          {agent.mode === "plan" && <ToneBadge tone="violet">Plan mode</ToneBadge>}
          {agent.timeoutMinutes != null && <Badge variant="outline">Timeout {agent.timeoutMinutes}m</Badge>}
          {agent.session.startsWith("reuse:") && (
            <ToneBadge tone="amber">Continues {agent.session.slice(6)}</ToneBadge>
          )}
          {agent.autoCreatePr && <ToneBadge tone="emerald">Opens PR</ToneBadge>}
          {agent.output === "issues" && <ToneBadge tone="violet">Files issues</ToneBadge>}
        </div>
        {section?.detached && <RunNow agent={agent} busy={(stats?.active ?? 0) + (stats?.queued ?? 0) > 0} />}
      </SheetHeader>
      <ScrollArea className="min-h-0 flex-1">
        <div className="@container fx-rise space-y-6 p-6">
          {/* One strip of three numbers, divided by hairlines rather than boxed one by one. */}
          <dl className="grid grid-cols-3 overflow-hidden rounded-lg border [&>*+*]:border-l">
            {(
              [
                ["Active", stats?.active ?? 0],
                ["Queued", stats?.queued ?? 0],
                ["Blocked", stats?.blocked ?? 0],
              ] as const
            ).map(([label, value]) => (
              <div key={label} className="px-3 py-2.5">
                <dt className="text-xs text-muted-foreground">{label}</dt>
                <dd className="mt-1 text-[22px] leading-none font-semibold tracking-[-0.02em] tabular-nums">
                  {value}
                </dd>
              </div>
            ))}
          </dl>

          <div className="grid gap-6 @2xl:grid-cols-2">
            <DetailSection title="Starts when">
              <ul className="space-y-1.5 text-sm">
                {agent.triggers.map((t) => (
                  <li key={JSON.stringify(t)} className="flex gap-2">
                    <ChevronRight className="mt-0.5 size-4 shrink-0 text-muted-foreground" />
                    {triggerText(t)}
                  </li>
                ))}
              </ul>
            </DetailSection>

            <DetailSection title="Sends work to">
              {section?.detached ? (
                <p className="flex gap-2 text-sm">
                  <Inbox className="mt-0.5 size-4 shrink-0 text-muted-foreground" />
                  New Skynet issues in the backlog start intake automatically.
                </p>
              ) : (
                <ul className="space-y-1.5 text-sm">
                  <li className="flex gap-2">
                    <CircleCheck className="mt-0.5 size-4 shrink-0 text-emerald-500" />
                    <span>
                      <span className="text-muted-foreground">On success:</span>{" "}
                      {describeNext(agent.routing.onSuccess.next, agentById, pipeline)}
                    </span>
                  </li>
                  <li className="flex gap-2">
                    <CircleX className="mt-0.5 size-4 shrink-0 text-red-500" />
                    <span>
                      <span className="text-muted-foreground">On failure:</span>{" "}
                      {describeNext(agent.routing.onFailure.next, agentById, pipeline)}
                    </span>
                  </li>
                  {Object.entries(agent.routing.onVerdict).map(([verdict, route]) => (
                    <li key={verdict} className="flex gap-2">
                      <Diamond className="mt-0.5 size-4 shrink-0 text-violet-500" />
                      <span>
                        <span className="text-muted-foreground">Verdict</span>{" "}
                        <code className="rounded bg-muted px-1 font-mono text-xs">{verdict}</code>:{" "}
                        {describeNext(route.next, agentById, pipeline)}
                      </span>
                    </li>
                  ))}
                </ul>
              )}
            </DetailSection>
          </div>

          {(agent.mcp.required.length > 0 || agent.mcp.optional.length > 0) && (
            <DetailSection
              title="Tools (MCP)"
              action={
                <Link to="/integrations" className="text-xs text-muted-foreground hover:text-foreground">
                  Manage integrations
                </Link>
              }
            >
              <div className="flex flex-wrap gap-1.5">
                {agent.mcp.required.map((id) => (
                  <Badge key={id} className="font-mono" title="Required">
                    {id}
                  </Badge>
                ))}
                {agent.mcp.optional.map((id) => (
                  <Badge key={id} variant="outline" className="font-mono" title="Optional">
                    {id}
                  </Badge>
                ))}
              </div>
              {agent.mcp.required.length > 0 && (
                <p className="text-xs text-muted-foreground">
                  Filled badges are required for the station to run.
                </p>
              )}
            </DetailSection>
          )}

          {agent.policy === "merge-queue" && (
            <DetailSection title="Merge queue">
              {mergeItems.length === 0 ? (
                <p className="text-sm text-muted-foreground">Queue is empty.</p>
              ) : (
                <div className="grid gap-3 @2xl:grid-cols-2">
                  {mergeItems.map((item) => (
                    <MergeTicket key={item.id} item={item} />
                  ))}
                </div>
              )}
            </DetailSection>
          )}

          <Separator />

          <DetailSection title="Recent runs">
            {runs.length === 0 ? (
              <p className="text-sm text-muted-foreground">No runs yet.</p>
            ) : (
              <ul className="-mx-2">
                {runs.map((run) => {
                  const task = tasks.get(run.taskId);
                  const key = task ? issueKey(task) : null;
                  return (
                    <li key={run.id}>
                      <Link
                        to={`/tasks/${run.taskId}`}
                        className="flex items-center gap-3 rounded-md px-2 py-2 text-sm hover:bg-muted"
                      >
                        <StatusBadge status={run.status} />
                        <span className="min-w-0 flex-1 truncate">
                          {key && (
                            <span className="mr-1.5 font-mono text-xs text-muted-foreground">{key}</span>
                          )}
                          {task?.title ?? run.taskId}
                        </span>
                        <span className="shrink-0 text-xs text-muted-foreground tabular-nums">
                          <TimeAgo at={run.updatedAt} />
                        </span>
                      </Link>
                    </li>
                  );
                })}
              </ul>
            )}
          </DetailSection>
        </div>
      </ScrollArea>
    </>
  );
}

/**
 * Starts a detached specialist against one of the active repos. The repo it
 * reads is the one it files issues into, and those enter the factory through intake.
 */
function RunNow({ agent, busy }: { agent: AgentSummary; busy: boolean }) {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const repos = useRepos();
  const [instructions, setInstructions] = useState("");
  // The agent's own repo when its markdown names one, else the default repo.
  const [repoKey, setRepoKey] = useState(agent.repoKey ?? DEFAULT_REPO);
  const chosen = repoKey === DEFAULT_REPO ? undefined : repos.find((repo) => repo.key === repoKey);
  const run = useMutation({
    mutationFn: () =>
      api.createTask({
        title: `${agent.name} run`,
        startAgentId: agent.id,
        instructions: instructions.trim() || undefined,
        repoKey: repoKey === DEFAULT_REPO ? undefined : repoKey,
      }),
    onSuccess: ({ task }) => {
      toast.success(`${agent.name} started`, { description: "Issues it files will arrive through intake." });
      queryClient.invalidateQueries({ queryKey: ["tasks"] });
      queryClient.invalidateQueries({ queryKey: ["state"] });
      navigate(`/tasks/${task.id}`);
    },
    onError: (error: Error) => toast.error(`Could not start ${agent.name}`, { description: error.message }),
  });
  return (
    <form
      className="space-y-1.5 pt-2"
      onSubmit={(e) => {
        e.preventDefault();
        run.mutate();
      }}
    >
      <div className="flex flex-wrap gap-2">
        <RepoSelect
          value={repoKey}
          onChange={setRepoKey}
          allowDefault={!agent.repoKey}
          className="w-52"
          ariaLabel={`${agent.name} repository`}
        />
        <Input
          className="min-w-40 flex-1"
          value={instructions}
          onChange={(e) => setInstructions(e.target.value)}
          placeholder="Operator instructions (optional)"
          aria-label="Operator instructions"
        />
        <Button type="submit" disabled={busy || run.isPending}>
          {run.isPending ? <Spinner /> : <Play />} Run now
        </Button>
      </div>
      {chosen && !chosen.active && (
        <p className="text-xs text-destructive">
          {chosen.name} is inactive. Activate it in Settings or pick another repo.
        </p>
      )}
      {busy && (
        <p className="text-xs text-muted-foreground">Already running. Start another once it finishes.</p>
      )}
    </form>
  );
}

function DetailSection({
  title,
  action,
  children,
}: {
  title: string;
  action?: React.ReactNode;
  children: React.ReactNode;
}) {
  return (
    <section className="space-y-2.5">
      <div className="flex items-center justify-between">
        <h3 className="text-sm font-medium">{title}</h3>
        {action}
      </div>
      {children}
    </section>
  );
}
